/**
 * Web Vitals Monitoring Hook
 *
 * Tracks Core Web Vitals and reports to monitoring system:
 * - FCP (First Contentful Paint)
 * - LCP (Largest Contentful Paint)
 * - CLS (Cumulative Layout Shift)
 * - TTFB (Time to First Byte)
 * - FID (First Input Delay)
 */

import { useEffect } from 'react';
import { onCLS, onFCP, onFID, onLCP, onTTFB, type Metric } from 'web-vitals';
import { supabase } from '@/integrations/supabase/client';

interface PerformanceMetrics {
  fcp_ms?: number;
  lcp_ms?: number;
  cls?: number;
  ttfb_ms?: number;
  fid_ms?: number;
}

export function useWebVitals() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const metrics: PerformanceMetrics = {};
    const sessionId = generateSessionId();
    let reported = false;

    // Collect metrics
    onFCP((metric) => handleMetric('FCP', metric, metrics));
    onLCP((metric) => handleMetric('LCP', metric, metrics));
    onCLS((metric) => handleMetric('CLS', metric, metrics));
    onTTFB((metric) => handleMetric('TTFB', metric, metrics));
    onFID((metric) => handleMetric('FID', metric, metrics));

    // Send metrics when page visibility changes or on unload
    const sendMetrics = () => {
      if (reported || Object.keys(metrics).length === 0) return;
      reported = true;
      sendPerformanceMetrics(sessionId, metrics);
    };

    // Send on visibility change (user switches tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendMetrics();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', sendMetrics);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', sendMetrics);
      sendMetrics();
    };
  }, []);
}

function handleMetric(
  name: string,
  metric: Metric,
  metrics: PerformanceMetrics
): void {
  switch (name) {
    case 'FCP':
      metrics.fcp_ms = Math.round(metric.value);
      break;
    case 'LCP':
      metrics.lcp_ms = Math.round(metric.value);
      break;
    case 'CLS':
      metrics.cls = Number(metric.value.toFixed(3));
      break;
    case 'TTFB':
      metrics.ttfb_ms = Math.round(metric.value);
      break;
    case 'FID':
      metrics.fid_ms = Math.round(metric.value);
      break;
  }
}

function sendPerformanceMetrics(
  sessionId: string,
  metrics: PerformanceMetrics
): void {
  // Get additional metrics from Performance API
  const navigationTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  const domLoadMs = navigationTiming ? Math.round(navigationTiming.domContentLoadedEventEnd) : undefined;
  const windowLoadMs = navigationTiming ? Math.round(navigationTiming.loadEventEnd) : undefined;

  // Get memory usage (if available)
  const memory = (performance as any).memory;
  const memoryUsedMb = memory
    ? Number((memory.usedJSHeapSize / 1024 / 1024).toFixed(1))
    : undefined;

  // Detect device type
  const deviceType = getDeviceType();

  // Get connection info
  const connection = (navigator as any).connection;
  const connectionType = connection?.effectiveType || 'unknown';

  // Send to monitoring system (fire and forget)
  supabase
    .from('client_performance_metrics')
    .insert({
      session_id: sessionId,
      page_url: window.location.pathname,
      fcp_ms: metrics.fcp_ms,
      lcp_ms: metrics.lcp_ms,
      cls: metrics.cls,
      ttfb_ms: metrics.ttfb_ms,
      fid_ms: metrics.fid_ms,
      dom_load_ms: domLoadMs,
      window_load_ms: windowLoadMs,
      memory_used_mb: memoryUsedMb,
      device_type: deviceType,
      browser: getBrowserInfo(),
      os: getOSInfo(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      connection_type: connectionType,
    })
    .then(({ error }) => {
      if (error) {
        console.error('[WebVitals] Failed to send metrics:', error);
      }
    });
}

function generateSessionId(): string {
  // Use existing session ID or create new one
  const storageKey = 'perf_session_id';
  let sessionId = sessionStorage.getItem(storageKey);

  if (!sessionId) {
    // Use crypto.randomUUID() for proper UUID v4 format
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      sessionId = crypto.randomUUID();
    } else {
      // Fallback: Generate UUID v4 format manually
      sessionId = generateUUIDv4();
    }
    sessionStorage.setItem(storageKey, sessionId);
  }

  return sessionId;
}

function generateUUIDv4(): string {
  // Generate UUID v4: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;

  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

function getBrowserInfo(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edge')) return 'Edge';
  return 'Unknown';
}

function getOSInfo(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS')) return 'iOS';
  return 'Unknown';
}
